import type { ScheduleGrid } from "features/transient-response/schedules/model/types";

import { Stack } from "@mui/material";
import { Button } from "components/ButtonNew";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { ScheduleHeatmapEditor } from "features/transient-response/schedules/components/ScheduleHeatmapEditor";
import { ScheduleHeatmapLegend } from "features/transient-response/schedules/components/ScheduleHeatmapLegend";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ROOM_SCHEDULE_MODES, type RoomScheduleMode } from "../model/scheduleTypes";

const ScheduleEmpty = () => {
  const { t } = useTranslation();
  return (
    <div className="relative flex min-h-[24rem] items-center justify-center">
      <p className="font-ds-body-semibold-lg">{t("TechnicalData.room.schedule.empty")}</p>
    </div>
  );
};

const SCHEDULE_RANGE = { max: 1, min: 0 };

type RoomScheduleHeatmapPanelProps = {
  grid: ScheduleGrid;
  hasData: boolean;
  isLoading?: boolean;
  mode: RoomScheduleMode;
  onEdit: () => void;
  onGridChange: (grid: ScheduleGrid) => void;
  onModeChange: (mode: RoomScheduleMode) => void;
};

export const RoomScheduleHeatmapPanel = ({
  grid,
  hasData,
  isLoading = false,
  mode,
  onEdit,
  onGridChange,
  onModeChange,
}: RoomScheduleHeatmapPanelProps) => {
  const { t } = useTranslation();
  const isCustom = mode === "custom";

  const handleGridChange = useCallback((nextGrid: ScheduleGrid) => onGridChange(nextGrid), [onGridChange]);

  return (
    <Stack className="w-full flex-col gap-4">
      <Stack className="items-center justify-between gap-3">
        <Stack className="flex-wrap gap-2">
          {ROOM_SCHEDULE_MODES.map((scheduleMode) => (
            <Button
              isActive={mode === scheduleMode}
              key={scheduleMode}
              onClick={() => onModeChange(scheduleMode)}
              size="sm"
              variant="secondary"
            >
              {t(`TechnicalData.room.schedule.modes.${scheduleMode}`)}
            </Button>
          ))}
        </Stack>
        {!isCustom && hasData && (
          <Button onClick={onEdit} size="sm" startIconName="Edit" variant="secondary">
            {t("general.edit")}
          </Button>
        )}
      </Stack>
      <LoadingOrEmptyWrapper
        emptyComponent={ScheduleEmpty}
        height="24rem"
        showEmpty={!isLoading && !hasData && !isCustom}
        showLoading={isLoading}
      >
        <ScheduleHeatmapEditor
          grid={grid}
          onGridChange={handleGridChange}
          range={SCHEDULE_RANGE}
          readOnly={!isCustom}
        />
        {isCustom && <ScheduleHeatmapLegend range={SCHEDULE_RANGE} />}
      </LoadingOrEmptyWrapper>
    </Stack>
  );
};
